import type { OnlineOrder, StationKOT } from '@/types'
import type { CustomerReceiptSettings } from '@/lib/printCustomerReceipt'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { kotSlipTable, newKotSlip, renderKotSlipText, type KotSlip } from '@/lib/kotSlip'
import { formatTimeHuman } from '@/lib/utils'

// The ONLINE ORDER slip uses the same structured slip + 32-col renderer as
// every other kitchen ticket (lib/kotSlip.ts, mirroring the backend), so it
// looks like everything else coming off the same thermal printer.

const FULFILLMENT_LABEL: Record<string, string> = {
  pickup: 'PICKUP',
  delivery: 'DELIVERY',
  dine_in: 'DINE IN',
}

/** "3:04 PM" from an ISO string; empty if unparseable (F-FE-N122: 12-hour clock). */
function placedStamp(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return formatTimeHuman(d)
}

/**
 * The structured ONLINE ORDER slip. Used for the pre-accept print (no order
 * number yet), the accept-moment print, and the accepted-card reprint — one
 * builder so all three read identically.
 */
export function buildOnlineOrderKotSlipData(order: OnlineOrder): KotSlip {
  const slip = newKotSlip('online', 'KITCHEN ORDER')
  slip.banners.push('*** ONLINE ORDER ***')
  const fulfil = FULFILLMENT_LABEL[order.fulfillment_type] ?? String(order.fulfillment_type ?? '').toUpperCase()
  if (fulfil) slip.banners.push(`>>> ${fulfil} <<<`)

  // F-BE-N54: the table, in the slip's dedicated Table slot — the same place
  // and the same prominence a counter-fired dine-in KOT puts it.
  //
  // It was missing entirely, so a QR order printed as ">>> DINE IN <<<" with
  // nothing saying which table. The kitchen could make the food and no one
  // could deliver it.
  //
  // Store the BARE number: both renderers prepend "TABLE " and upper-case it
  // themselves (kotSlip.ts), so writing "TABLE 88" here prints "TABLE TABLE 88".
  // kotSlipTable also filters the placeholders ("N/A", "—") that mean no table,
  // which is what keeps a takeaway slip from carrying an empty table banner.
  slip.table = kotSlipTable(order.table_number ?? '')

  // order_number is only present once accepted; pre-accept slips say so.
  slip.meta.push({
    left: order.order_number ? `#${order.order_number}` : '#NOT YET ACCEPTED',
    right: 'Online Order',
  })
  const customer = (order.customer_name ?? '').trim()
  const phone = (order.customer_phone ?? '').trim()
  if (customer || phone) slip.meta.push({ left: customer, right: phone })
  const address = (order.delivery_address ?? '').trim()
  if (address) slip.meta.push({ left: address, right: '' })
  const placed = placedStamp(order.created_at)
  if (placed) slip.meta.push({ left: 'Placed', right: placed })

  for (const ln of (order.notes ?? '').trim().split('\n')) {
    const clean = ln.replace(/[ \t\r]+$/, '')
    if (clean) slip.note.push(clean)
  }

  for (const item of order.cart) {
    const si = (item.special_instructions ?? '').trim()
    // The customer's options (F-MOD-N9). Without them two "Wings" lines at
    // different prices print identically and the kitchen guesses the flavour.
    const mods = (item.modifier_labels ?? []).map((m) => m.trim()).filter(Boolean)
    slip.items.push({
      name: item.name,
      qty: item.quantity,
      ...(mods.length > 0 ? { mods } : {}),
      ...(si ? { note: si } : {}),
    })
  }
  return slip
}

/** The 32-col text payload of the ONLINE ORDER slip (ESC/POS path). */
export function buildOnlineOrderKotPayload(order: OnlineOrder): string {
  return renderKotSlipText(buildOnlineOrderKotSlipData(order))
}

/**
 * Synthesize a single printer StationKOT for an online order so it flows through
 * the existing thermal-print pipeline (printKotReceipts → desktop silent print
 * or browser iframe dialog) unchanged. No backend fire — this is a paper slip
 * only; the KDS/kitchen routing happens on Accept in the backend.
 */
export function buildOnlineOrderKotSlip(order: OnlineOrder): StationKOT {
  const slip = buildOnlineOrderKotSlipData(order)
  return {
    station_id: 'online',
    station_name: 'Online Order',
    output_type: 'printer',
    print_location: 'kitchen',
    printer_target: '',
    payload: renderKotSlipText(slip),
    slip,
  }
}

/** Print an ONLINE ORDER KOT slip using the tenant's KOT paper/font settings. */
export async function printOnlineOrderKot(
  order: OnlineOrder,
  cfg: Pick<CustomerReceiptSettings, 'paperWidthMm' | 'printableAreaMm' | 'kotFontScale'>,
): Promise<void> {
  await printKotReceipts([buildOnlineOrderKotSlip(order)], cfg)
}
